import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Cookies from 'js-cookie';
import { parseJwt } from '@common/utils';
import { usePostHog } from 'posthog-js/react';
import useAuthStore from '@/store/AuthStore';
import { ECookieKey, OrganizationRoles } from '@/enums';
import { useGetOrganizationsOnLogin, useGetUserWithToken } from '@/reactQuery/get';
import useUserStore from '@/store/UserStore';
import Routes from '@/router/routes';
import { AuthData, Organization } from '@/types';

export function useAuthentication() {
  const navigate = useNavigate();
  const location = useLocation();
  const [redirectTo, setRedirectTo] = useState('/');
  const [error, setError] = useState<string>('');
  const sessionExpiredRedirectionLink = localStorage.getItem('sessionExpiredRedirectTo');
  const posthog = usePostHog();

  const from = location.state?.from || redirectTo;

  const { authToken, setAuthToken, setRequireTwoFactor } = useAuthStore();
  const { setUser } = useUserStore();
  const { mutateAsync: fetchOrganizations } = useGetOrganizationsOnLogin();
  const { mutateAsync: getUserWithToken } = useGetUserWithToken();

  const getValidOrganization = (organizations: Organization[], userId: string): Organization => {
    const workspace = organizations.find((organization: Organization) =>
      organization.members.some(
        ({ user_id, role }) => user_id === userId && role === OrganizationRoles.OWNER,
      ),
    );

    if (workspace && !workspace.isHidden) {
      return workspace;
    }

    return organizations.find((userWorkspace) =>
      userWorkspace.members.some(({ user_id }) => user_id === userId),
    );
  };

  // Set login cookies
  const setLoginCookies = (data: AuthData, remember: boolean = true) => {
    const options = { expires: remember ? 14 : 1 };
    const cookiesToSet = {
      [ECookieKey.TOKEN]: data?.token,
      [ECookieKey.FALLBACK_WORKSPACE]: data?.mainWorkspace,
      [ECookieKey.MAIN_ORGANIZATION]: data?._id,
      [ECookieKey.REMEMBER_ME]: remember.toString(),
    };

    Object.entries(cookiesToSet).forEach(([key, value]) => {
      if (value) {
        Cookies.set(key, value, options);
      }
    });
  };

  // Process authentication success and handle navigation
  // eslint-disable-next-line consistent-return
  const processAuthSuccess = async (token: string, remember: boolean = true) => {
    try {
      if (!token) {
        setError('Authentication failed. Please try again.');
        return null;
      }

      // Set token in auth store
      setAuthToken(token);

      // Parse token to get user ID
      const parsedToken = parseJwt(token);
      if (!parsedToken || !parsedToken.userId) {
        setError('Authentication failed. Invalid credentials.');
        return null;
      }

      // Fetch organizations and user data in parallel
      const [{ data: organizations }, userData] = await Promise.all([
        fetchOrganizations(token),
        getUserWithToken(token),
      ]);

      // Store user data in Zustand to avoid duplicate API calls
      if (userData) {
        setUser(userData);
      }

      // Determine current organization and workspace for PostHog identification
      const last_organization = userData?.last_organization;
      const last_workspace = userData?.last_workspace;

      // Get valid workspace
      const validOrganization = getValidOrganization(organizations, parsedToken.userId);
      if (!validOrganization) {
        setError('No valid organization found. Please contact support.');
        return null;
      }

      const { _id, mainWorkspace } = validOrganization || {};

      // Make sure we have values for organization and workspace
      if (!_id) {
        setError('Failed to get organization details. Please contact support.');
        return null;
      }

      if (!mainWorkspace) {
        setError('Failed to get workspace details. Please contact support.');
        return null;
      }

      const currentOrganization = last_organization || _id;
      const currentWorkspace = last_workspace || mainWorkspace;

      // Identify user with PostHog including organization and workspace context
      if (userData && posthog) {
        try {
          posthog.identify(userData._id, {
            email: userData.email,
            organization_id: currentOrganization,
            workspace_id: currentWorkspace,
            user_role: userData.role,
          });
        } catch {
          // Analytics failure should not break the application
        }
      }

      // Set cookies
      setLoginCookies(
        {
          token,
          _id: currentOrganization,
          mainWorkspace: currentWorkspace,
        },
        remember,
      );

      if (sessionExpiredRedirectionLink) {
        // If session expired redirection page is set, navigate to that page
        navigate(sessionExpiredRedirectionLink, { replace: true });
        localStorage.removeItem('sessionExpiredRedirectTo');
        return null;
      }

      // Set redirect path
      const redirectPath = `${Routes.organization.path}/${currentOrganization}${Routes.workspace.path}/${currentWorkspace}`;
      setRedirectTo(redirectPath);

      // Navigate
      navigate(from, { replace: true });
    } catch (err) {
      setError('Failed to process authentication. Please try again.');
      console.error('Authentication processing error:', err);
      return null;
    }
  };

  // Handle 2FA required response
  const handleTwoFactorRequired = (userId: string) => {
    setRequireTwoFactor(true);
    return userId;
  };

  // Auto-redirect on token presence
  useEffect(() => {
    if (authToken) {
      navigate(from, { replace: true });
    }
  }, [authToken, navigate, from]);

  return {
    authToken,
    setAuthToken,
    setLoginCookies,
    processAuthSuccess,
    handleTwoFactorRequired,
    redirectTo,
    from,
    navigate,
    error,
    setError,
  };
}

export default useAuthentication;
